import type { CellObject, SheetData } from "write-excel-file/browser";
import type { Entry } from "@/shared/cache";
import type {
	ClientRow,
	DailyChartPoint,
	DashboardStats,
	MatterRow,
	MemberRow,
} from "../hooks/useReportingDashboard";
import { toDateKey } from "../hooks/useReportingDashboard";
import type { FieldOptions } from "./Entries/filters/FilterMenu";
import {
	FIELD_DEFS,
	getValueLabel,
	normalizeOptions,
} from "./Entries/filters/fields";
import type {
	EntryFilterState,
	EnumFilterField,
} from "./Entries/filters/types";

type ReportingDashboardExportData = {
	firmName: string;
	dateRange: { from: Date; to: Date };
	filters: EntryFilterState;
	fieldOptions: FieldOptions;
	stats: DashboardStats;
	dailyChart: DailyChartPoint[];
	topClients: ClientRow[];
	topMatters: MatterRow[];
	memberTable: MemberRow[];
	entries?: Entry[];
};

type SheetValue = string | number | null;

type StyledCell = CellObject;

const FILTER_FIELDS: EnumFilterField[] = [
	"status",
	"timekeeper",
	"application",
	"client",
	"matter",
	"billableCategory",
];

function roundHours(value: number): number {
	return Math.round((Number(value) || 0) * 10) / 10;
}

function money(value: number | null | undefined): number | null {
	if (value == null || !Number.isFinite(value)) return null;
	return Math.round(value * 100) / 100;
}

function formatDate(dateKey: string): string {
	const [year, month, day] = dateKey.split("-").map(Number);
	if (!year || !month || !day) return dateKey;
	return `${String(month).padStart(2, "0")}/${String(day).padStart(2, "0")}/${year}`;
}

function humanize(value: string | null | undefined): string {
	if (!value) return "";
	return value
		.split("_")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

function getFilterValues(
	filters: EntryFilterState,
	field: EnumFilterField,
): string[] | undefined {
	if (field === "status") return filters.status;
	return filters[field];
}

function describeFilterValues(
	field: EnumFilterField,
	values: string[] | undefined,
	fieldOptions: FieldOptions,
): string {
	if (values === undefined) return "All";
	if (values.length === 0) return "None";

	const optionLabels = new Map(
		normalizeOptions(fieldOptions[field] ?? []).map((option) => [
			option.value,
			option.label,
		]),
	);

	return values
		.map((value) => optionLabels.get(value) ?? getValueLabel(field, value))
		.join(", ");
}

export function getReportingExcelFilename(dateRange: {
	from: Date;
	to: Date;
}): string {
	return `reporting_${toDateKey(dateRange.from)}_to_${toDateKey(dateRange.to)}.xlsx`;
}

function cell(
	value: string | number | boolean | Date | null,
	style: Omit<StyledCell, "value"> = {},
): CellObject {
	if (value == null) return { ...style };
	if (typeof value === "number") return { value, type: Number, ...style };
	if (typeof value === "boolean") return { value, type: Boolean, ...style };
	if (value instanceof Date) return { value, type: Date, ...style };
	return { value, type: String, ...style };
}

const listingFont = "Times New Roman";

const headerStyle = {
	fontFamily: listingFont,
	fontSize: 14,
	fontWeight: "bold" as const,
	alignVertical: "top" as const,
	bottomBorderStyle: "medium" as const,
	wrap: true,
};

const bodyStyle = {
	fontFamily: listingFont,
	fontSize: 13,
	alignVertical: "top" as const,
};

function buildSheetTitleRows(
	firmName: string,
	title: string,
	columnCount: number,
): SheetData {
	return [
		[
			cell(firmName, {
				fontFamily: listingFont,
				fontSize: 28,
				fontWeight: "bold",
				align: "center",
				columnSpan: columnCount,
			}),
		],
		[
			cell(title, {
				fontFamily: listingFont,
				fontSize: 24,
				fontWeight: "bold",
				align: "center",
				columnSpan: columnCount,
			}),
		],
	];
}

function buildFormattedSheetRows(
	firmName: string,
	title: string,
	rows: SheetValue[][],
	columnCount: number,
): SheetData {
	return [
		...buildSheetTitleRows(firmName, title, columnCount),
		...rows.map((row, rowIndex) =>
			row.map((value) =>
				cell(
					value,
					rowIndex === 0 ? headerStyle : { ...bodyStyle, wrap: true },
				),
			),
		),
	];
}

function initials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

function timekeeperLookup(fieldOptions: FieldOptions): Map<string, string> {
	return new Map(
		normalizeOptions(fieldOptions.timekeeper ?? []).map((option) => [
			option.value,
			option.label,
		]),
	);
}

function entryTimekeeperName(
	entry: Entry,
	lookup: Map<string, string>,
): string {
	return (entry.timekeeperId && lookup.get(entry.timekeeperId)) || "";
}

function buildListingRows(
	firmName: string,
	entries: Entry[],
	fieldOptions: FieldOptions,
): SheetData {
	const rows: SheetData = [
		...buildSheetTitleRows(firmName, "Listing", 8),
		[
			cell("Date", { ...headerStyle, align: "center" }),
			cell("Prof", { ...headerStyle, align: "center" }),
			cell("Matter ID/Client Sort\nMatter Description\nNarrative", headerStyle),
			cell("Activity Code", headerStyle),
			cell("Component\nTask Code", headerStyle),
			cell("Units", { ...headerStyle, align: "right" }),
			cell("Price", { ...headerStyle, align: "right" }),
			cell("Value", { ...headerStyle, align: "right" }),
		],
	];

	const lookup = timekeeperLookup(fieldOptions);
	const sorted = [...entries].sort((a, b) => {
		const dateCompare = a.date.localeCompare(b.date);
		if (dateCompare !== 0) return dateCompare;
		return entryTimekeeperName(a, lookup).localeCompare(
			entryTimekeeperName(b, lookup),
		);
	});

	let lastDate = "";
	let lastTimekeeper = "";
	for (const entry of sorted) {
		const date = entry.date;
		const timekeeperName = entryTimekeeperName(entry, lookup);

		if (date !== lastDate) {
			rows.push([
				cell(`Date: ${formatDate(date)}`, {
					fontFamily: listingFont,
					fontSize: 14,
					fontWeight: "bold",
					columnSpan: 8,
					topBorderStyle: "medium",
				}),
			]);
			lastDate = date;
			lastTimekeeper = "";
		}

		if (timekeeperName && timekeeperName !== lastTimekeeper) {
			rows.push([
				null,
				cell(`Professional: ${timekeeperName}`, {
					fontFamily: listingFont,
					fontSize: 14,
					fontWeight: "bold",
					columnSpan: 7,
				}),
			]);
			lastTimekeeper = timekeeperName;
		}

		const matterLine = [entry.matter?.code, entry.matter?.client]
			.filter(Boolean)
			.join("/ ");
		const details = [
			matterLine,
			entry.matter?.description ?? "",
			entry.description,
		]
			.filter(Boolean)
			.join("\n");

		rows.push([
			cell(formatDate(date), { ...bodyStyle, align: "center" }),
			cell(initials(timekeeperName), { ...bodyStyle, align: "center" }),
			cell(details, { ...bodyStyle, wrap: true }),
			cell(entry.codes.activity ?? "", bodyStyle),
			cell(entry.codes.task ? `T\n${entry.codes.task}` : "", {
				...bodyStyle,
				wrap: true,
			}),
			cell(roundHours(entry.units), {
				...bodyStyle,
				align: "right",
				format: "0.00",
			}),
			cell(null, { ...bodyStyle, align: "right" }),
			cell(money(null), { ...bodyStyle, align: "right" }),
		]);
	}

	return rows;
}

export async function exportReportingDashboardToExcel(
	data: ReportingDashboardExportData,
): Promise<void> {
	const { default: writeExcelFile } = await import("write-excel-file/browser");
	const fromKey = toDateKey(data.dateRange.from);
	const toKey = toDateKey(data.dateRange.to);

	const overviewRows: SheetValue[][] = [
		["Metric", "Value"],
		["Date From", fromKey],
		["Date To", toKey],
		["Total Hours", roundHours(data.stats.totalHours)],
		["Exported Hours", roundHours(data.stats.exportedHours)],
		["To Export Hours", roundHours(data.stats.trackedHours)],
		["Entry Count", data.stats.entryCount],
		["Matter Count", data.stats.matterCount],
		["Average Per Weekday", roundHours(data.stats.avgPerDay)],
		[null, null],
		["Filter", "Selection"],
		...FILTER_FIELDS.map((field) => {
			const def = FIELD_DEFS.find((item) => item.key === field);
			return [
				def?.label ?? humanize(field),
				describeFilterValues(
					field,
					getFilterValues(data.filters, field),
					data.fieldOptions,
				),
			];
		}),
	];

	const dailyRows: SheetValue[][] = [
		["Date", "Exported Hours", "To Export Hours", "Total Hours"],
		...data.dailyChart.map((point) => [
			point.date,
			roundHours(point.exported),
			roundHours(point.tracked),
			roundHours(point.exported + point.tracked),
		]),
	];

	const clientRows: SheetValue[][] = [
		["Client", "Hours", "Exported Hours", "To Export Hours", "Entries"],
		...data.topClients.map((client) => [
			client.client,
			roundHours(client.hours),
			roundHours(client.exported),
			roundHours(client.tracked),
			client.entries,
		]),
	];

	const matterRows: SheetValue[][] = [
		[
			"Matter Number",
			"Matter Description",
			"Client",
			"Hours",
			"Entries",
			"Billing Category",
			"Billable Category",
		],
		...data.topMatters.map((matter) => [
			matter.matterNumber || matter.matterId,
			matter.matterDescription,
			matter.client,
			roundHours(matter.hours),
			matter.entries,
			humanize(matter.billingCategory),
			humanize(matter.billableCategory),
		]),
	];

	const timekeeperRows: SheetValue[][] = [
		[
			"Name",
			"Type",
			"To Export Hours",
			"Exported Hours",
			"Total Hours",
			"Entries",
			"Matters",
			"Average Per Day",
		],
		...data.memberTable.map((member) => [
			member.profName,
			member.profType ?? "",
			roundHours(member.tracked),
			roundHours(member.exported),
			roundHours(member.total),
			member.entries,
			member.matters,
			roundHours(member.avgPerDay),
		]),
	];

	const sheets = [
		...(data.entries
			? [
					{
						sheet: "Listing",
						data: buildListingRows(
							data.firmName,
							data.entries,
							data.fieldOptions,
						),
						columns: [14, 12, 48, 18, 18, 12, 12, 12].map((width) => ({
							width,
						})),
					},
				]
			: []),
		{
			sheet: "Overview",
			data: buildFormattedSheetRows(data.firmName, "Overview", overviewRows, 2),
			columns: [24, 60].map((width) => ({ width })),
		},
		{
			sheet: "Daily Hours",
			data: buildFormattedSheetRows(data.firmName, "Daily Hours", dailyRows, 4),
			columns: [14, 16, 18, 14].map((width) => ({ width })),
		},
		{
			sheet: "Clients",
			data: buildFormattedSheetRows(data.firmName, "Clients", clientRows, 5),
			columns: [36, 12, 16, 18, 12].map((width) => ({ width })),
		},
		{
			sheet: "Matters",
			data: buildFormattedSheetRows(data.firmName, "Matters", matterRows, 7),
			columns: [18, 48, 36, 12, 12, 20, 20].map((width) => ({ width })),
		},
	];

	if (data.memberTable.length > 0) {
		sheets.push({
			sheet: "Timekeepers",
			data: buildFormattedSheetRows(
				data.firmName,
				"Timekeepers",
				timekeeperRows,
				8,
			),
			columns: [32, 18, 18, 16, 14, 12, 12, 16].map((width) => ({
				width,
			})),
		});
	}

	await writeExcelFile(sheets).toFile(
		getReportingExcelFilename(data.dateRange),
	);
}
